<template>
  <div class="omx-stat" :class="'omx-stat--' + tone">
    <div class="omx-stat__icon" v-if="icon">
      <b-icon :icon="icon" size="is-small" />
    </div>
    <div class="omx-stat__label">{{ label }}</div>
    <div class="omx-stat__value">
      <slot>{{ value }}</slot>
    </div>
    <div class="omx-stat__delta" :class="deltaClass" v-if="delta || $slots.delta">
      <slot name="delta">{{ delta }}</slot>
    </div>
  </div>
</template>

<script>
/**
 * OmxStatTile — KPI tile for detail-page summary rows.
 *
 *   <div class="omx-stat-row">
 *     <OmxStatTile icon="clock-outline"
 *                  :label="$t('project.hours')" value="1,820 h" delta="+62 today" />
 *     <OmxStatTile icon="currency-eur" tone="green"
 *                  :label="$t('project.profit')" value="+€8,420" delta="+4.2%" />
 *     <OmxStatTile icon="account-multiple" tone="warm" delta-direction="down"
 *                  :label="$t('project.external')" value="38%" delta="target 30%" />
 *     <OmxStatTile icon="flag-outline" tone="purple"
 *                  :label="$t('project.tasks')" value="17 of 42" delta="on track" />
 *   </div>
 *
 * Tones: 'brand' (default) | 'green' | 'warm' | 'purple' | 'neutral'.
 * deltaDirection: 'up' (success) | 'down' (danger) | 'flat' (default).
 */
export default {
  name: "OmxStatTile",
  props: {
    icon:           { type: String, default: null },
    label:          { type: String, default: null },
    value:          { type: [String, Number], default: null },
    delta:          { type: String, default: null },
    tone:           { type: String, default: "brand" },
    deltaDirection: { type: String, default: "up" }
  },
  computed: {
    deltaClass() {
      return 'is-' + (this.deltaDirection || 'up');
    }
  }
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/tokens";

.omx-stat {
  position: relative;
  background: $omx-surface;
  border: 1px solid $omx-ink-100;
  border-radius: $omx-radius-md;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  overflow: hidden;
}

.omx-stat__icon {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: $omx-brand-50;
  color: $omx-brand-600;
}

.omx-stat--green .omx-stat__icon  { background: $omx-success-100; color: $omx-success-600; }
.omx-stat--warm  .omx-stat__icon  { background: $omx-accent-100;  color: $omx-accent-600; }
.omx-stat--purple .omx-stat__icon { background: #efe9fb;          color: #6f3ed4; }
.omx-stat--neutral .omx-stat__icon { background: $omx-ink-100;    color: $omx-ink-700; }

.omx-stat__label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .1em;
  text-transform: uppercase;
  color: $omx-ink-500;
  padding-right: 36px;
}

.omx-stat__value {
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -.012em;
  color: $omx-ink-900;
  font-variant-numeric: tabular-nums;
}

.omx-stat__delta {
  font-size: 12px;
  font-weight: 600;
  &.is-up   { color: $omx-success-600; }
  &.is-down { color: $omx-danger-600; }
  &.is-flat { color: $omx-ink-500; }
}

.omx-stat-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
  margin-bottom: 18px;
}

@media (max-width: 900px) {
  .omx-stat-row { grid-template-columns: repeat(2, 1fr); }
}
</style>
